import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { searchApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { useQuery } from "@tanstack/react-query";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  suggestionKey,
  suggestionLink,
  suggestionTypeLabel,
} from "../searchLinks";

type Presentation = "play" | "mobile";

/** A bounded, owner-authorized typeahead; typing alone never changes routes. */
export function QuickSearchField({
  presentation,
  autoFocus = false,
  onNavigate,
}: {
  presentation: Presentation;
  autoFocus?: boolean;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const [value, setValue] = useState("");
  const [settled, setSettled] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<number | "all">(-1);
  const normalized = value.trim();

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(normalized), 280);
    return () => window.clearTimeout(timer);
  }, [normalized]);

  const ready = open && normalized.length >= 2 && settled === normalized;
  const preview = useQuery({
    queryKey: ["player-quick-search", user?.uid ?? "guest", settled],
    queryFn: () => searchApi.suggestions(settled, "all"),
    enabled: ready,
    staleTime: 30_000,
  });
  const items = ready ? (preview.data?.suggestions.slice(0, 8) ?? []) : [];
  const show = ready;
  const allUrl = `/search?${new URLSearchParams({ q: normalized })}`;

  function close() {
    setOpen(false);
    setActive(-1);
  }

  function selected() {
    close();
    setValue("");
    onNavigate?.();
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!normalized) {
      inputRef.current?.focus();
      return;
    }
    selected();
    navigate(allUrl);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      close();
      return;
    }
    if (!show) return;
    const optionCount = items.length + 1; // Include “See all results”.
    const activeIndex = active === "all" ? items.length : active;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = (activeIndex + 1) % optionCount;
      setActive(next === items.length ? "all" : next);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const next = activeIndex <= 0 ? optionCount - 1 : activeIndex - 1;
      setActive(next === items.length ? "all" : next);
    } else if (event.key === "Enter" && active !== -1) {
      event.preventDefault();
      optionRefs.current[active === "all" ? items.length : active]?.click();
    }
  }

  return (
    <Box
      component="form"
      role="search"
      aria-label={presentation === "play" ? "Search everything" : "Site search"}
      onSubmit={submit}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: presentation === "mobile" ? "column" : "row",
        alignItems: "stretch",
        gap: 1,
        width: "100%",
        maxWidth: undefined,
      }}
    >
      <TextField
        inputRef={inputRef}
        autoFocus={autoFocus}
        type="search"
        size="small"
        fullWidth
        placeholder={presentation === "mobile" ? "Name or place" : "Search coaches, facilities, classes…"}
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          setActive(-1);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={(event) => {
          if (event.relatedTarget instanceof Node && listRef.current?.contains(event.relatedTarget)) return;
          close();
        }}
        onKeyDown={handleKeyDown}
        slotProps={{
          htmlInput: {
            "aria-label": "Search everything",
            "aria-autocomplete": "list",
            "aria-expanded": show,
            "aria-controls": show ? listId : undefined,
            "aria-activedescendant": active !== -1 ? `${listId}-${active === "all" ? items.length : active}` : undefined,
          },
        }}
        sx={undefined}
      />
      {show ? (
        <Paper
          component="ul"
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label="Quick search results"
          elevation={5}
          onBlur={(event) => {
            if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) close();
          }}
          sx={{
            position: presentation === "mobile" ? "static" : "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            p: 0.5,
            m: 0,
            listStyle: "none",
            maxHeight: 320,
            overflowY: "auto",
            borderRadius: 2,
          }}
        >
          {preview.isPending ? (
            <Box component="li" role="presentation" sx={{ px: 1.5, py: 1 }}>
              <Typography variant="body2" color="text.secondary">Searching…</Typography>
            </Box>
          ) : preview.isError ? (
            <Box component="li" role="presentation" sx={{ px: 1.5, py: 1 }}>
              <Typography variant="body2" color="text.secondary">Quick matches unavailable.</Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box component="li" role="presentation" sx={{ px: 1.5, py: 1 }}>
              <Typography variant="body2" color="text.secondary">No quick matches.</Typography>
            </Box>
          ) : null}
          {items.map((item, index) => (
            <Box component="li" role="option" aria-selected={active === index} id={`${listId}-${index}`} key={suggestionKey(item)} sx={{ listStyle: "none" }}>
              <Box
                component={RouterLink}
                ref={(element: HTMLAnchorElement | null) => { optionRefs.current[index] = element; }}
                to={suggestionLink(item)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={selected}
                sx={{ display: "flex", justifyContent: "space-between", gap: 1, px: 1.5, py: 1, color: "text.primary", textDecoration: "none", borderRadius: 1, bgcolor: active === index ? "action.selected" : "transparent", "&:hover, &:focus-visible": { bgcolor: "action.hover" } }}
              >
                <span>{item.label}</span>
                <Typography component="span" variant="caption" color="text.secondary">{suggestionTypeLabel(item)}</Typography>
              </Box>
            </Box>
          ))}
          <Box component="li" role="option" aria-selected={active === "all"} id={`${listId}-${items.length}`} sx={{ listStyle: "none" }}>
            <Box
              component={RouterLink}
              ref={(element: HTMLAnchorElement | null) => { optionRefs.current[items.length] = element; }}
              to={allUrl}
              onMouseDown={(event) => event.preventDefault()}
              onClick={selected}
              sx={{ display: "block", px: 1.5, py: 1, color: "primary.main", textDecoration: "none", borderRadius: 1, bgcolor: active === "all" ? "action.selected" : "transparent", "&:hover, &:focus-visible": { bgcolor: "action.hover" } }}
            >
              See all results
            </Box>
          </Box>
        </Paper>
      ) : null}
      {presentation !== "mobile" ? (
        <IconButton type="submit" aria-label="Search" color="inherit">
          <SearchRoundedIcon fontSize="small" aria-hidden="true" />
        </IconButton>
      ) : (
        <Button type="submit" variant="contained" size="small" sx={{ flexShrink: 0 }}>
          Search
        </Button>
      )}
    </Box>
  );
}
